import React, { useId, useRef } from 'react';
import useModalFocus from '../../hooks/useModalFocus';
import './AppModal.css';

/**
 * AppModal — global Promise-based modal.
 *
 * config shape:
 * {
 *   icon:        string | 'success'|'error'|'warning'|'info'   (optional)
 *   title:       string                                         (optional)
 *   message:     string                                         (optional)
 *   dismissable: boolean  default false — overlay click closes  (optional)
 *   buttons: [
 *     { label: string, value: any, variant: 'primary'|'ghost'|'cancel'|'danger' }
 *   ]
 * }
 *
 * Usage:
 *   const choice = await modal({ icon:'✅', title:'...', buttons:[...] });
 */
const AppModal = ({ config, onResolve, iconPreset }) => {
  const rootRef = useRef(null);
  const titleId = useId();
  const messageId = useId();
  useModalFocus(rootRef, () => { if (config?.dismissable) onResolve(null); }, Boolean(config));
  if (!config) return null;

  const {
    icon,
    title,
    message,
    buttons     = [],
    dismissable = false,
  } = config;

  // Resolve preset shorthand ('success' → '✅') or use the value as-is
  const resolvedIcon = iconPreset[icon] ?? icon ?? null;

  return (
    <div
      ref={rootRef}
      className="am-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      aria-describedby={message ? messageId : undefined}
      tabIndex={-1}
      onClick={() => dismissable && onResolve(null)}
    >
      <div className="am-card" onClick={e => e.stopPropagation()}>

        {resolvedIcon && (
          <span className="am-icon" aria-hidden="true">{resolvedIcon}</span>
        )}

        {title   && <h3 id={titleId} className="am-title">{title}</h3>}
        {message && <p id={messageId} className="am-message">{message}</p>}

        {buttons.length > 0 && (
          <div className="am-actions">
            {buttons.map((btn, i) => (
              <button
                key={i}
                className={`am-btn am-btn--${btn.variant || 'ghost'}`}
                onClick={() => onResolve(btn.value)}
              >
                {btn.label}
              </button>
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default AppModal;
